import React from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Folder,
  FolderOpen,
  CornerLeftUp,
  MoreVertical,
  Share2,
  Link,
  LoaderCircle,
  FolderInput,
  Users,
  Download,
} from 'lucide-react'
import FileTypeIcon from '../../../components/file-manager/FileIcon'
import { useTranslation } from 'react-i18next'
import { formatBytes } from '../../../utils/formatters'
import { cn } from '@/lib/utils'
import { levelAtLeast } from '../utils/permission-levels'

const rowLevelAtLeast = (folder: any, min: string) =>
  levelAtLeast(folder?.effectivePermission?.level, min)

interface FolderType {
  id: string
  name: string
  description: string | null
  parentId: string | null
  documentCount: number
  activeShareLinkCount: number
}

interface Document {
  id: string
  name: string
  description: string | null
  filename: string
  mimetype: string
  size: number
  revisionCount: number
  activeShareLinkCount: number
  currentRevision?: {
    id: string
    version: number
    fileId: string
    file?: {
      id: string
      size: number
      mimetype: string
    }
  }
}

interface FileManagerGridProps {
  folders: FolderType[]
  documents: Document[]
  loading: boolean
  currentFolderId: string | null
  parentFolderId: string | null
  onFolderClick: (folderId: string | null) => void
  onDocumentClick: (documentId: string) => void
  /** Opens the detail dialog; falls back to `onDocumentClick` when absent (Freelo #32223790). */
  onDocumentDetails?: (documentId: string) => void
  onEditFolder?: (folderId: string) => void
  onDeleteFolder?: (folder: FolderType) => void
  onMoveFolder?: (folder: FolderType) => void
  onShareFolder?: (folder: FolderType) => void
  onManageAccess?: (folder: FolderType) => void
  onDownloadFolder?: (folder: FolderType) => void
  downloadingFolderId?: string | null
  onDownloadDocument?: (document: Document) => void
  onDeleteDocument?: (document: Document) => void
  onMoveDocument?: (document: Document) => void
  onShareDocument?: (document: Document) => void
  onCopyFolderLink?: (folder: FolderType) => void
  onCopyDocumentLink?: (document: Document) => void
  selection?: string[]
  onSelectionChange?: (selection: string[]) => void
}

const FileManagerGrid: React.FC<FileManagerGridProps> = ({
  folders,
  documents,
  loading,
  currentFolderId,
  parentFolderId,
  onFolderClick,
  onDocumentClick,
  onDocumentDetails,
  onEditFolder,
  onDeleteFolder,
  onMoveFolder,
  onShareFolder,
  onManageAccess,
  onDownloadFolder,
  downloadingFolderId,
  onDownloadDocument,
  onDeleteDocument,
  onMoveDocument,
  onShareDocument,
  onCopyFolderLink,
  onCopyDocumentLink,
  selection = [],
  onSelectionChange,
}) => {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const isEmpty = folders.length === 0 && documents.length === 0 && !currentFolderId

  if (isEmpty) {
    return (
      <div className="text-center text-muted-foreground py-12">
        <Folder className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="mb-1">{t('DocControl_no_folders_yet')}</p>
        <p className="text-xs">{t('DocControl_create_folder_hint')}</p>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2">
        {/* Go Up (..) */}
        {currentFolderId && (
          <div
            className="group flex flex-col items-center gap-1 rounded-md border border-transparent p-3 cursor-pointer transition-colors hover:bg-muted hover:border-border"
            onClick={() => onFolderClick(parentFolderId)}
          >
            <CornerLeftUp className="h-10 w-10 text-muted-foreground" />
            <span className="text-xs font-medium">..</span>
          </div>
        )}

        {/* Folders */}
        {folders.map((folder) => (
          <div
            key={folder.id}
            className="group flex flex-col items-center gap-1 rounded-md border border-transparent p-3 cursor-pointer transition-colors hover:bg-accent hover:border-border relative"
            onClick={() => onFolderClick(folder.id)}
          >
            {/* Context menu */}
            {(onManageAccess || onCopyFolderLink || onDownloadFolder ||
              (onEditFolder && rowLevelAtLeast(folder, 'EDITOR')) ||
              (onMoveFolder && rowLevelAtLeast(folder, 'EDITOR')) ||
              (onShareFolder && rowLevelAtLeast(folder, 'ADMIN')) ||
              (onDeleteFolder && rowLevelAtLeast(folder, 'MANAGER'))) && (
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onFolderClick(folder.id)}>
                      {t('Common_open')}
                    </DropdownMenuItem>
                    {onEditFolder && rowLevelAtLeast(folder, 'EDITOR') && (
                      <DropdownMenuItem onClick={() => onEditFolder(folder.id)}>
                        {t('Common_edit')}
                      </DropdownMenuItem>
                    )}
                    {onShareFolder && rowLevelAtLeast(folder, 'ADMIN') && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onShareFolder(folder)}>
                          {t('DocControl_share')}
                        </DropdownMenuItem>
                      </>
                    )}
                    {onManageAccess && (
                      <DropdownMenuItem onClick={() => onManageAccess(folder)}>
                        <Users className="h-3.5 w-3.5" />
                        {t('DocControl_manage_access', 'Manage access')}
                      </DropdownMenuItem>
                    )}
                    {onCopyFolderLink && (
                      <DropdownMenuItem onClick={() => onCopyFolderLink(folder)}>
                        <Link className="h-3.5 w-3.5" />
                        {t('DocControl_copy_link')}
                      </DropdownMenuItem>
                    )}
                    {onDownloadFolder && (
                      <DropdownMenuItem
                        onClick={() => onDownloadFolder(folder)}
                        disabled={downloadingFolderId === folder.id}
                      >
                        {downloadingFolderId === folder.id ? (
                          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        {t('DocControl_download_folder')}
                      </DropdownMenuItem>
                    )}
                    {onMoveFolder && rowLevelAtLeast(folder, 'EDITOR') && (
                      <DropdownMenuItem onClick={() => onMoveFolder(folder)}>
                        <FolderInput className="h-3.5 w-3.5" />
                        {t('DocControl_move_to')}
                      </DropdownMenuItem>
                    )}
                    {onDeleteFolder && rowLevelAtLeast(folder, 'MANAGER') && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDeleteFolder(folder)}>
                          {t('Common_delete')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <Folder className="h-10 w-10 text-amber-400" />
            <span
              className="text-center text-xs font-medium leading-tight max-w-full line-clamp-2"
              title={folder.name}
            >
              {folder.name}
            </span>
            {(folder.activeShareLinkCount > 0 || folder.documentCount > 0) && (
              <div className="flex items-center gap-1">
                {folder.activeShareLinkCount > 0 && (
                  <Share2 className="h-3 w-3 text-cyan-500" />
                )}
                {folder.documentCount > 0 && (
                  <span className="text-[10px] text-muted-foreground">{folder.documentCount} {t('DocControl_files_count')}</span>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Documents */}
        {documents.map((doc) => {
          const isSelected = selection.includes(doc.id)
          return (
            <div
              key={doc.id}
              className={cn(
                'group flex flex-col items-center gap-1 rounded-md border p-3 cursor-pointer transition-colors relative',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:bg-accent hover:border-border'
              )}
              onClick={() => onDocumentClick(doc.id)}
            >
              {/* Selection checkbox */}
              {onSelectionChange && (
                <div
                  className="absolute top-1 left-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => {
                      if (!onSelectionChange) return
                      if (selection.includes(doc.id)) {
                        onSelectionChange(selection.filter((id) => id !== doc.id))
                      } else {
                        onSelectionChange([...selection, doc.id])
                      }
                    }}
                    className="h-4 w-4 cursor-pointer"
                  />
                </div>
              )}

              {/* Context menu */}
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => (onDocumentDetails ?? onDocumentClick)(doc.id)}>
                      {t('DocControl_view_details')}
                    </DropdownMenuItem>
                    {onDownloadDocument && (
                      <DropdownMenuItem onClick={() => onDownloadDocument(doc)}>
                        {t('Common_download')}
                      </DropdownMenuItem>
                    )}
                    {onShareDocument && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onShareDocument(doc)}>
                          {t('DocControl_share')}
                        </DropdownMenuItem>
                      </>
                    )}
                    {onCopyDocumentLink && (
                      <DropdownMenuItem onClick={() => onCopyDocumentLink(doc)}>
                        <Link className="h-3.5 w-3.5" />
                        {t('DocControl_copy_link')}
                      </DropdownMenuItem>
                    )}
                    {onMoveDocument && (
                      <DropdownMenuItem onClick={() => onMoveDocument(doc)}>
                        <FolderInput className="h-3.5 w-3.5" />
                        {t('DocControl_move_to')}
                      </DropdownMenuItem>
                    )}
                    {onDeleteDocument && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDeleteDocument(doc)}>
                          {t('Common_delete')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <FileTypeIcon filename={doc.filename} size={40} />
              <span
                className="text-center text-xs font-medium leading-tight max-w-full line-clamp-2"
                title={doc.name}
              >
                {doc.name}
              </span>
              <div className="flex items-center gap-1">
                {doc.activeShareLinkCount > 0 && (
                  <Share2 className="h-3 w-3 text-cyan-500" />
                )}
                <span className="text-[10px] text-muted-foreground">
                  {formatBytes(doc.currentRevision?.file?.size ?? doc.size)}
                </span>
              </div>
            </div>
          )
        })}

        {/* Empty folder message — inline next to go-up */}
        {currentFolderId && folders.length === 0 && documents.length === 0 && (
          <div className="flex items-center gap-2 text-muted-foreground self-center whitespace-nowrap">
            <FolderOpen className="h-5 w-5 opacity-50 shrink-0" />
            <span>{t('DocControl_folder_empty')}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default FileManagerGrid
